/**
 * Thin client for ZITADEL's Management API + v2 Resource API. Reads the
 * current access token from the singleton UserManager and attaches it as
 * Bearer. All calls route through the Vite `/zitadel/*` proxy so the
 * browser sees same-origin and CORS isn't a problem in local dev.
 *
 * Two API surfaces — both still active in v4:
 *   - v2 (`/v2/users/...`)         — newer typed REST. Preferred for user CRUD.
 *   - v1 (`/management/v1/...`)    — older. Required for user-grant ops
 *                                    (project-role assignment), which v2
 *                                    doesn't cover yet.
 *
 * Requires:
 *   - The signed-in user has `ORG_USER_MANAGER` (or higher) in ZITADEL,
 *     otherwise every call returns 403. The UI surfaces the error via toast.
 *   - `VITE_ZITADEL_PROJECT_ID` set to the "XAES Mission Control" project
 *     ID — needed for role-grant ops.
 *
 * Error contract: throws `ZitadelApiError` with the HTTP status + the
 * Zitadel error message extracted from the response body. Callers catch
 * and toast.
 */

import { env, isMultiClientEnabled } from "@/env";
import { clientOrgIdFromClaims } from "@/features/auth/roles";
import { getCurrentUser } from "@/features/auth/user-manager";

const BASE = "/zitadel";

export class ZitadelApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = "ZitadelApiError";
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const user = await getCurrentUser();
  if (!user || !user.access_token) {
    throw new ZitadelApiError(401, "Not signed in");
  }
  return { Authorization: `Bearer ${user.access_token}` };
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(await authHeader()),
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let message = res.statusText;
    let code: string | undefined;
    try {
      const payload = (await res.json()) as { message?: string; code?: string };
      if (payload.message) message = payload.message;
      if (payload.code) code = payload.code;
    } catch {
      /* response body not JSON — keep the statusText */
    }
    throw new ZitadelApiError(res.status, message, code);
  }

  // 204 No Content — return empty object so callers can `await` without
  // tripping on `res.json()` of an empty body.
  if (res.status === 204) return {} as T;
  return (await res.json()) as T;
}

/* ── Types (subset of the Zitadel REST shapes we actually consume) ────── */

export interface ZitadelUserSummary {
  /** Stable user ID. Use this for every other call. */
  userId: string;
  /** preferred_username / email-style login name. */
  username: string;
  human?: {
    profile?: {
      givenName?: string;
      familyName?: string;
      displayName?: string;
    };
    email?: {
      email?: string;
      isVerified?: boolean;
    };
  };
  state?: string;
}

interface ListUsersResponse {
  result?: ZitadelUserSummary[];
  details?: { totalResult?: string };
}

export interface UserGrant {
  /** Stable grant ID. Use this for update / delete. */
  id: string;
  userId: string;
  projectId: string;
  roleKeys: string[];
}

interface ListGrantsResponse {
  result?: UserGrant[];
}

/* ── User CRUD (v2 where available) ───────────────────────────────────── */

export async function listUsers(): Promise<ZitadelUserSummary[]> {
  // v2 search — empty body returns everything the caller may see, which
  // Zitadel's permission model already bounds. With multi-client on we
  // additionally pin the search to the caller's RESIDENT org (multi-client
  // plan, roster-leak item): the roster grid must never enumerate another
  // client org's users even for a caller Zitadel would permit (e.g. an
  // org manager with cross-org grants). Belt-and-braces on top of the
  // server-side model, same spirit as the BFF's fail-closed rules.
  let body: Record<string, unknown> = {};
  if (isMultiClientEnabled()) {
    const orgId = clientOrgIdFromClaims(await getCurrentUser());
    if (orgId) {
      body = {
        queries: [{ organizationIdQuery: { organizationId: orgId } }],
      };
    }
  }
  const data = await request<ListUsersResponse>("POST", "/v2/users", body);
  return data.result ?? [];
}

export interface CreateHumanUserInput {
  username: string;
  email: string;
  givenName: string;
  familyName: string;
  password: string;
  passwordChangeRequired?: boolean;
  emailVerified?: boolean;
}

export async function createHumanUser(
  input: CreateHumanUserInput,
): Promise<{ userId: string }> {
  // v2 typed REST. The shape is nested: profile + email + password are
  // separate objects per the OpenAPI spec.
  const body = {
    username: input.username,
    profile: {
      givenName: input.givenName,
      familyName: input.familyName,
    },
    email: {
      email: input.email,
      isVerified: input.emailVerified ?? true,
    },
    password: {
      password: input.password,
      changeRequired: input.passwordChangeRequired ?? false,
    },
  };
  return await request<{ userId: string }>("POST", "/v2/users/human", body);
}

export async function updateHumanUser(
  userId: string,
  updates: {
    username?: string;
    givenName?: string;
    familyName?: string;
    /** Also pushed when given/family change — ZITADEL doesn't auto-recompute. */
    displayName?: string;
  },
): Promise<void> {
  const body: Record<string, unknown> = {};
  if (updates.username !== undefined) body.username = updates.username;
  const hasProfile =
    updates.givenName !== undefined ||
    updates.familyName !== undefined ||
    updates.displayName !== undefined;
  if (hasProfile) {
    body.profile = {
      ...(updates.givenName !== undefined ? { givenName: updates.givenName } : {}),
      ...(updates.familyName !== undefined ? { familyName: updates.familyName } : {}),
      ...(updates.displayName !== undefined ? { displayName: updates.displayName } : {}),
    };
  }
  await request("PUT", `/v2/users/human/${encodeURIComponent(userId)}`, body);
}

export async function setUserPassword(
  userId: string,
  password: string,
  changeRequired = false,
): Promise<void> {
  await request("POST", `/v2/users/${encodeURIComponent(userId)}/password`, {
    newPassword: { password, changeRequired },
  });
}

export async function deactivateUser(userId: string): Promise<void> {
  // v2 deactivate is a POST with empty body.
  await request("POST", `/v2/users/${encodeURIComponent(userId)}/deactivate`, {});
}

export async function reactivateUser(userId: string): Promise<void> {
  await request("POST", `/v2/users/${encodeURIComponent(userId)}/reactivate`, {});
}

/* ── User grants (v1 — v2 doesn't cover this yet) ─────────────────────── */

/**
 * Bulk list of all user-grants the caller can see (whole org). Avoids the
 * N+1 of calling `listUserGrants` for every user in the roster.
 */
export async function listAllUserGrants(): Promise<UserGrant[]> {
  const data = await request<ListGrantsResponse>(
    "POST",
    `/management/v1/users/grants/_search`,
    {},
  );
  return (data.result ?? []).map((g) => ({
    id: g.id,
    userId: (g as { userId?: string }).userId ?? "",
    projectId: (g as { projectId?: string }).projectId ?? "",
    roleKeys: (g as { roleKeys?: string[] }).roleKeys ?? [],
  }));
}

/**
 * List grants for a specific user. Zitadel v4 doesn't expose a per-user
 * search endpoint (POST /management/v1/users/{id}/grants/_search returns
 * 405). We fetch all grants the caller can see and filter client-side.
 * Volume is small on a single-tenant local instance, and this keeps us
 * resilient to filter-shape drift across Zitadel versions.
 */
export async function listUserGrants(userId: string): Promise<UserGrant[]> {
  const all = await listAllUserGrants();
  return all.filter((g) => g.userId === userId);
}

export async function addUserGrant(
  userId: string,
  projectId: string,
  roleKeys: string[],
): Promise<{ userGrantId: string }> {
  return await request<{ userGrantId: string }>(
    "POST",
    `/management/v1/users/${encodeURIComponent(userId)}/grants`,
    { projectId, roleKeys },
  );
}

export async function updateUserGrant(
  userId: string,
  grantId: string,
  roleKeys: string[],
): Promise<void> {
  await request(
    "PUT",
    `/management/v1/users/${encodeURIComponent(userId)}/grants/${encodeURIComponent(grantId)}`,
    { roleKeys },
  );
}

export async function removeUserGrant(
  userId: string,
  grantId: string,
): Promise<void> {
  await request(
    "DELETE",
    `/management/v1/users/${encodeURIComponent(userId)}/grants/${encodeURIComponent(grantId)}`,
  );
}

/* ── Convenience helpers used by the hooks ────────────────────────────── */

/**
 * Replace a user's roles for the XAES project. If they have an existing
 * grant we update it in place (preserves the grant ID); otherwise we
 * create a new grant.
 */
export async function setProjectRolesForUser(
  userId: string,
  roleKeys: string[],
): Promise<void> {
  if (!env.VITE_ZITADEL_PROJECT_ID) {
    throw new ZitadelApiError(
      500,
      "VITE_ZITADEL_PROJECT_ID is not set — required for role assignment",
    );
  }
  const grants = await listUserGrants(userId);
  const existing = grants.find(
    (g) => g.projectId === env.VITE_ZITADEL_PROJECT_ID,
  );
  if (existing) {
    if (roleKeys.length === 0) {
      await removeUserGrant(userId, existing.id);
    } else {
      await updateUserGrant(userId, existing.id, roleKeys);
    }
  } else if (roleKeys.length > 0) {
    await addUserGrant(userId, env.VITE_ZITADEL_PROJECT_ID, roleKeys);
  }
}
